/**
 * CDM Change Pipeline — Track 6B active-remediation surface.
 *
 * Three tabs:
 *  - Open proposals: kanban by state (proposed / awaiting_approval / approved
 *                    / executed / rolled_back). Card carries target_kind,
 *                    target_ref, proposed_by, days-since.
 *  - Executions:     timeline of recent ChangeExecution rows with status
 *                    pills, before/after diff, rollback button (red, two-key).
 *  - New proposal:   form with target_kind dropdown (loaded from
 *                    /api/cdm/changes/target-kinds), target_ref free-text,
 *                    intent + payload (JSON) + reasoning.
 *
 * PHASE A: the /target-kinds payload includes a `live` flag — we surface a
 * banner when CDM_CHANGE_PIPELINE_LIVE=false so operators know the executor
 * is logging-only.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  GitPullRequest, ChevronLeft, RefreshCw, Play, CheckCircle2, XCircle,
  Lock, RotateCcw, AlertTriangle, Clock, Send,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface TargetKind {
  target_kind: string
  description: string
  requires_approval: boolean
  two_key: boolean
  rollback_window_days: number
  intents: string[]
  executor: string | null
  stub: boolean
  wired: boolean
}

interface Proposal {
  id: string
  org_id: string
  target_kind: string
  target_ref: string
  intent: string
  payload: any
  proposed_by: string
  proposed_at: string | null
  reasoning: string | null
  correlation_id: string | null
  state: string
}

interface Approval {
  id: string
  proposal_id: string
  approver_id: string
  decision: string
  decided_at: string | null
  notes: string | null
}

interface Execution {
  id: string
  proposal_id: string
  started_at: string | null
  completed_at: string | null
  executor: string
  request_payload: any
  response_payload: any
  response_status: number | null
  source_value_before: any
  source_value_after: any
  state: string
  error: string | null
  rollback_token: string | null
}

const STATE_TONE: Record<string, string> = {
  proposed:           'bg-text-muted/15 text-text-muted border-border',
  awaiting_approval:  'bg-amber/15 text-amber-light border-amber/30',
  approved:           'bg-blue/15 text-blue-light border-blue/30',
  rejected:           'bg-rose/15 text-rose-light border-rose/30',
  executed:           'bg-emerald/15 text-emerald border-emerald/30',
  failed:             'bg-rose/15 text-rose-light border-rose/30',
  rolled_back:        'bg-text-muted/15 text-text-muted border-border',
}

const EXEC_STATE_TONE: Record<string, string> = {
  queued:        'bg-text-muted/15 text-text-muted border-border',
  preflight:     'bg-blue/15 text-blue-light border-blue/30',
  executing:     'bg-blue/15 text-blue-light border-blue/30',
  verified:      'bg-emerald/15 text-emerald border-emerald/30',
  failed_silent: 'bg-rose/15 text-rose-light border-rose/30',
  failed_error:  'bg-rose/15 text-rose-light border-rose/30',
  rolled_back:   'bg-text-muted/15 text-text-muted border-border',
}

const KANBAN_COLUMNS = [
  'proposed',
  'awaiting_approval',
  'approved',
  'executed',
  'rolled_back',
] as const

type Tab = 'proposals' | 'executions' | 'new'

export default function CdmChangePipelinePage() {
  const [tab, setTab] = useState<Tab>('proposals')
  const [targetKinds, setTargetKinds] = useState<TargetKind[]>([])
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [executions, setExecutions] = useState<Execution[]>([])
  const [live, setLive] = useState(false)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [activeProposal, setActiveProposal] = useState<Proposal | null>(null)
  const [activeApprovals, setActiveApprovals] = useState<Approval[]>([])
  const [activeExecutions, setActiveExecutions] = useState<Execution[]>([])

  // New-proposal form state
  const [formKind, setFormKind] = useState('')
  const [formIntent, setFormIntent] = useState('')
  const [formRef, setFormRef] = useState('')
  const [formReasoning, setFormReasoning] = useState('')
  const [formPayload, setFormPayload] = useState('{}')

  const load = () => {
    setLoading(true)
    const q = new URLSearchParams()
    if (stateFilter) q.set('state', stateFilter)
    Promise.all([
      api.get('/api/cdm/changes/target-kinds'),
      api.get('/api/cdm/changes/proposals?' + q.toString()),
      api.get('/api/cdm/changes/executions?limit=200'),
    ])
      .then(([t, p, e]) => {
        setTargetKinds(t.data?.target_kinds || [])
        setLive(Boolean(t.data?.live))
        setProposals(p.data?.rows || [])
        setExecutions(e.data?.rows || [])
      })
      .catch(err => setMessage(`Load failed: ${err.response?.data?.detail || err.message}`))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [stateFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = async (p: Proposal) => {
    setActiveProposal(p)
    try {
      const { data } = await api.get(`/api/cdm/changes/proposals/${p.id}`)
      setActiveApprovals(data?.approvals || [])
      setActiveExecutions(data?.executions || [])
    } catch (e: any) {
      setMessage(`Detail load failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const closeDetail = () => {
    setActiveProposal(null)
    setActiveApprovals([])
    setActiveExecutions([])
  }

  const propose = async () => {
    if (!formKind || !formIntent || !formRef.trim()) {
      setMessage('target_kind, intent, target_ref are all required')
      return
    }
    let parsedPayload: any = {}
    try { parsedPayload = formPayload ? JSON.parse(formPayload) : {} }
    catch { setMessage('payload must be valid JSON'); return }

    setBusy('propose')
    try {
      const { data } = await api.post('/api/cdm/changes/propose', {
        target_kind: formKind,
        target_ref:  formRef.trim(),
        intent:      formIntent,
        payload:     parsedPayload,
        reasoning:   formReasoning || null,
      })
      setMessage(`Proposed: ${data.proposal_id} (state=${data.state}${data.live ? '' : ' · DRY-RUN'})`)
      setFormRef(''); setFormReasoning(''); setFormPayload('{}')
      setTab('proposals')
      load()
    } catch (e: any) {
      setMessage(`Propose failed: ${e.response?.data?.detail || e.message}`)
    } finally { setBusy(null) }
  }

  const decide = async (proposalId: string, decision: 'approve' | 'reject', notes?: string) => {
    setBusy(`decide-${proposalId}`)
    try {
      const { data } = await api.post(`/api/cdm/changes/proposals/${proposalId}/approve`,
                                      { decision, notes: notes || null })
      setMessage(`Decision recorded: ${decision} → state=${data.state} (${data.n_approvals}/${data.approvals_needed} approvals)`)
      load()
      if (activeProposal?.id === proposalId) loadDetail(activeProposal)
    } catch (e: any) {
      setMessage(`Decide failed: ${e.response?.data?.detail || e.message}`)
    } finally { setBusy(null) }
  }

  const execute = async (proposalId: string) => {
    if (!confirm('Execute this proposal now? Two-key approval and maintenance window are checked server-side.')) return
    setBusy(`exec-${proposalId}`)
    try {
      const { data } = await api.post(`/api/cdm/changes/proposals/${proposalId}/execute`)
      setMessage(`Executed: ${data.execution_id} (state=${data.state}${data.live ? '' : ' · DRY-RUN'})`)
      load()
      if (activeProposal?.id === proposalId) loadDetail(activeProposal)
    } catch (e: any) {
      setMessage(`Execute failed: ${e.response?.data?.detail || e.message}`)
    } finally { setBusy(null) }
  }

  const rollback = async (executionId: string) => {
    const code = prompt('Two-key confirm: type ROLLBACK to proceed.')
    if (code !== 'ROLLBACK') { setMessage('Rollback cancelled — confirmation phrase did not match.'); return }
    setBusy(`rb-${executionId}`)
    try {
      const { data } = await api.post(`/api/cdm/changes/executions/${executionId}/rollback`)
      setMessage(`Rollback ${data.ok ? 'OK' : 'FAILED'}: execution=${data.execution_id}`)
      load()
      if (activeProposal) loadDetail(activeProposal)
    } catch (e: any) {
      setMessage(`Rollback failed: ${e.response?.data?.detail || e.message}`)
    } finally { setBusy(null) }
  }

  const proposalsByState = useMemo(() => {
    const out: Record<string, Proposal[]> = {}
    for (const col of KANBAN_COLUMNS) out[col] = []
    for (const p of proposals) {
      const k = KANBAN_COLUMNS.includes(p.state as any) ? p.state : 'proposed'
      ;(out[k] = out[k] || []).push(p)
    }
    return out
  }, [proposals])

  const formMeta = targetKinds.find(t => t.target_kind === formKind)

  const counts = {
    proposals:  proposals.length,
    executions: executions.length,
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <GitPullRequest size={18} className="text-cyan" /> CDM CHANGE PIPELINE
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          active remediation · proposals · approvals · executions
        </span>
        <div className="ml-auto flex items-center gap-2">
          {!live && (
            <span className="text-[10px] uppercase tracking-widest border border-amber/40 bg-amber/10 text-amber-light px-2 py-1 rounded inline-flex items-center gap-1">
              <Lock size={11} /> Phase A · Dry-run (CDM_CHANGE_PIPELINE_LIVE=false)
            </span>
          )}
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      {/* Tab strip */}
      <div className="flex items-center gap-1 border-b border-border">
        <TabButton active={tab === 'proposals'} onClick={() => setTab('proposals')}
                   icon={GitPullRequest} label={`Open proposals · ${counts.proposals}`} />
        <TabButton active={tab === 'executions'} onClick={() => setTab('executions')}
                   icon={Play} label={`Executions · ${counts.executions}`} />
        <TabButton active={tab === 'new'} onClick={() => setTab('new')}
                   icon={Send} label="New proposal" />
      </div>

      {/* Open proposals — kanban */}
      {tab === 'proposals' && (
        <div className="space-y-3">
          <div className="card p-3 text-[11px] text-text-muted">
            Kanban view of every active ChangeProposal. Cards carry the
            target_kind badge, target_ref short-hash, and days since
            proposed. Click a card for the full audit trail (approvals +
            executions) and admin actions. Two-key target_kinds require
            two distinct approvers before <code>execute()</code> will run.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {KANBAN_COLUMNS.map(col => (
              <div key={col} className="card p-2 space-y-2 min-h-[180px]">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-text-muted">
                  <span className={clsx('px-1.5 py-0.5 rounded border', STATE_TONE[col])}>
                    {col.replace(/_/g, ' ')}
                  </span>
                  <span className="ml-auto">{(proposalsByState[col] || []).length}</span>
                </div>
                {(proposalsByState[col] || []).map(p => (
                  <button key={p.id} type="button" onClick={() => loadDetail(p)}
                          className="w-full text-left bg-surface-raised hover:bg-surface-raised/70 border border-border rounded p-2 text-xs space-y-1">
                    <div className="font-mono text-[10px] text-cyan-light truncate">
                      {p.target_kind}
                    </div>
                    <div className="font-mono text-[10px] text-text-secondary truncate">
                      {p.target_ref}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-text-muted">
                      <span className="truncate">{p.proposed_by}</span>
                      <span className="ml-auto">{daysSince(p.proposed_at)}</span>
                    </div>
                  </button>
                ))}
                {(proposalsByState[col] || []).length === 0 && (
                  <div className="text-[10px] text-text-muted/60 italic px-1">empty</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Executions tab */}
      {tab === 'executions' && (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised">
              <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">Executor</th>
                <th className="text-left px-3 py-2">Proposal</th>
                <th className="text-left px-3 py-2">Before → After</th>
                <th className="text-left px-3 py-2">Started</th>
                <th className="text-left px-3 py-2">Completed</th>
                <th className="text-left px-3 py-2 w-px"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {executions.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-text-muted">
                  No executions yet.
                </td></tr>
              )}
              {executions.map(e => (
                <tr key={e.id} className="hover:bg-surface-raised/40">
                  <td className="px-3 py-1.5">
                    <span className={clsx('px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider',
                                          EXEC_STATE_TONE[e.state] || 'bg-text-muted/15 text-text-muted border-border')}>
                      {e.state}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{e.executor}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-text-muted">
                    {e.proposal_id.slice(0, 8)}…
                  </td>
                  <td className="px-3 py-1.5">
                    <DiffCell before={e.source_value_before} after={e.source_value_after} />
                  </td>
                  <td className="px-3 py-1.5 text-[10px] text-text-muted">
                    {e.started_at ? new Date(e.started_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-[10px] text-text-muted">
                    {e.completed_at ? new Date(e.completed_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {(e.state === 'verified' || e.state === 'failed_silent') && (
                      <button type="button" onClick={() => rollback(e.id)} disabled={!!busy}
                              className="text-[10px] uppercase tracking-widest text-rose-light hover:text-rose-light/80 inline-flex items-center gap-1 px-2 py-1 border border-rose/40 rounded">
                        <RotateCcw size={10} /> Rollback
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New proposal form */}
      {tab === 'new' && (
        <div className="card p-4 space-y-3 max-w-2xl">
          <p className="text-[11px] text-text-muted">
            Submit a new ChangeProposal. Admins can propose + approve +
            execute end-to-end; agents can only propose. Phase A pilot:
            CrowdStrike isolate (two-key approval, 7-day rollback window).
          </p>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-text-muted">Target kind</label>
            <select value={formKind} onChange={e => { setFormKind(e.target.value); setFormIntent('') }}
                    className="w-full bg-surface-raised text-xs border border-border rounded px-2 py-1.5">
              <option value="">— select —</option>
              {targetKinds.map(t => (
                <option key={t.target_kind} value={t.target_kind} disabled={!t.wired}>
                  {t.target_kind}{t.stub ? ' (stub — not wired)' : ''}
                </option>
              ))}
            </select>
            {formMeta && (
              <div className="text-[10px] text-text-secondary border border-border bg-surface-raised rounded px-2 py-1.5 space-y-0.5">
                <div>{formMeta.description}</div>
                <div className="flex items-center gap-3 text-text-muted">
                  {formMeta.requires_approval && <span className="inline-flex items-center gap-1"><Lock size={9} /> approval required</span>}
                  {formMeta.two_key && <span className="inline-flex items-center gap-1"><Lock size={9} /> two-key</span>}
                  <span>rollback window: {formMeta.rollback_window_days}d</span>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-text-muted">Intent</label>
            <select value={formIntent} onChange={e => setFormIntent(e.target.value)}
                    disabled={!formMeta}
                    className="w-full bg-surface-raised text-xs border border-border rounded px-2 py-1.5">
              <option value="">— select —</option>
              {(formMeta?.intents || []).map(i => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-text-muted">Target ref</label>
            <input type="text" value={formRef} onChange={e => setFormRef(e.target.value)}
                   placeholder="e.g. CrowdStrike machine_id (44-char hex)"
                   className="w-full bg-surface-raised text-xs font-mono border border-border rounded px-2 py-1.5" />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-text-muted">Payload (JSON)</label>
            <textarea value={formPayload} onChange={e => setFormPayload(e.target.value)}
                      rows={3}
                      className="w-full bg-surface-raised text-xs font-mono border border-border rounded px-2 py-1.5"
                      placeholder='{"duration_minutes": 60, "reason": "incident-1234"}' />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-text-muted">Reasoning</label>
            <textarea value={formReasoning} onChange={e => setFormReasoning(e.target.value)}
                      rows={3}
                      className="w-full bg-surface-raised text-xs border border-border rounded px-2 py-1.5"
                      placeholder="Why is this change being proposed?" />
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={propose} disabled={busy === 'propose'}
                    className="btn-primary text-xs">
              <Send size={11} /> Propose
            </button>
            {!live && (
              <span className="text-[10px] text-amber-light">
                Dry-run mode — execution will log only, no real API call.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {activeProposal && (
        <div className="fixed inset-0 z-40 bg-black/50 flex justify-end" onClick={closeDetail}>
          <div className="bg-surface-card border-l border-border w-full max-w-2xl h-full overflow-y-auto p-4 space-y-3"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-baseline gap-3 pb-2 border-b border-border">
              <span className={clsx('px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider',
                                    STATE_TONE[activeProposal.state] || 'bg-text-muted/15 text-text-muted border-border')}>
                {activeProposal.state}
              </span>
              <h2 className="text-sm font-semibold text-text-primary">
                {activeProposal.target_kind}
              </h2>
              <button type="button" onClick={closeDetail} className="ml-auto text-text-muted hover:text-text-primary">
                <XCircle size={16} />
              </button>
            </div>

            <dl className="grid grid-cols-3 gap-2 text-[11px]">
              <DT label="Target ref"><code className="font-mono text-[10px] break-all">{activeProposal.target_ref}</code></DT>
              <DT label="Intent">{activeProposal.intent}</DT>
              <DT label="Proposed by">{activeProposal.proposed_by}</DT>
              <DT label="Proposed at">{activeProposal.proposed_at ? new Date(activeProposal.proposed_at).toLocaleString() : '—'}</DT>
              <DT label="Correlation">
                {activeProposal.correlation_id ? <code className="font-mono text-[10px]">{activeProposal.correlation_id.slice(0, 8)}…</code> : '—'}
              </DT>
              <DT label="ID"><code className="font-mono text-[10px]">{activeProposal.id.slice(0, 8)}…</code></DT>
            </dl>

            {activeProposal.reasoning && (
              <div className="text-[11px] text-text-secondary border border-border bg-surface-raised rounded p-2">
                <div className="text-[9px] uppercase tracking-widest text-text-muted mb-1">Reasoning</div>
                {activeProposal.reasoning}
              </div>
            )}

            {Object.keys(activeProposal.payload || {}).length > 0 && (
              <div className="text-[11px] border border-border bg-surface-raised rounded p-2">
                <div className="text-[9px] uppercase tracking-widest text-text-muted mb-1">Payload</div>
                <pre className="font-mono text-[10px] whitespace-pre-wrap break-all">{JSON.stringify(activeProposal.payload, null, 2)}</pre>
              </div>
            )}

            {/* Action row */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
              {(activeProposal.state === 'proposed' || activeProposal.state === 'awaiting_approval') && (
                <>
                  <button type="button" onClick={() => decide(activeProposal.id, 'approve')}
                          disabled={!!busy}
                          className="btn-primary text-[11px]">
                    <CheckCircle2 size={11} /> Approve
                  </button>
                  <button type="button" onClick={() => decide(activeProposal.id, 'reject')}
                          disabled={!!busy}
                          className="text-[11px] uppercase tracking-widest text-rose-light hover:text-rose-light/80 inline-flex items-center gap-1 px-3 py-1.5 border border-rose/40 rounded">
                    <XCircle size={11} /> Reject
                  </button>
                </>
              )}
              {activeProposal.state === 'approved' && (
                <button type="button" onClick={() => execute(activeProposal.id)}
                        disabled={!!busy}
                        className="btn-primary text-[11px]">
                  <Play size={11} /> Execute
                </button>
              )}
            </div>

            {/* Approvals */}
            <div className="space-y-1">
              <div className="text-[9px] uppercase tracking-widest text-text-muted">Approvals · {activeApprovals.length}</div>
              {activeApprovals.length === 0 && <div className="text-[10px] text-text-muted italic">no approvals yet</div>}
              {activeApprovals.map(a => (
                <div key={a.id} className="flex items-center gap-2 text-[10px] border border-border bg-surface-raised rounded px-2 py-1">
                  {a.decision === 'approve' ? <CheckCircle2 size={10} className="text-emerald" /> : <XCircle size={10} className="text-rose-light" />}
                  <span>{a.approver_id}</span>
                  <span className="text-text-muted ml-auto">
                    {a.decided_at ? new Date(a.decided_at).toLocaleString() : '—'}
                  </span>
                </div>
              ))}
            </div>

            {/* Executions */}
            <div className="space-y-1">
              <div className="text-[9px] uppercase tracking-widest text-text-muted">Executions · {activeExecutions.length}</div>
              {activeExecutions.length === 0 && <div className="text-[10px] text-text-muted italic">no executions yet</div>}
              {activeExecutions.map(e => (
                <div key={e.id} className="border border-border bg-surface-raised rounded p-2 space-y-1">
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className={clsx('px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-wider',
                                          EXEC_STATE_TONE[e.state] || 'bg-text-muted/15 text-text-muted border-border')}>
                      {e.state}
                    </span>
                    <code className="font-mono text-[9px] text-text-muted">{e.id.slice(0, 8)}…</code>
                    {e.response_status != null && <span className="text-text-secondary">HTTP {e.response_status}</span>}
                    <span className="ml-auto text-text-muted">
                      {e.completed_at ? new Date(e.completed_at).toLocaleString() :
                       e.started_at ? new Date(e.started_at).toLocaleString() : '—'}
                    </span>
                  </div>
                  <DiffCell before={e.source_value_before} after={e.source_value_after} />
                  {e.error && <div className="text-[10px] text-rose-light"><AlertTriangle size={9} className="inline" /> {e.error}</div>}
                  {(e.state === 'verified' || e.state === 'failed_silent') && (
                    <button type="button" onClick={() => rollback(e.id)} disabled={!!busy}
                            className="text-[10px] uppercase tracking-widest text-rose-light hover:text-rose-light/80 inline-flex items-center gap-1 px-2 py-1 border border-rose/40 rounded">
                      <RotateCcw size={10} /> Rollback (two-key)
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


function TabButton({ active, onClick, icon: Icon, label }: {
  active: boolean
  onClick: () => void
  icon: React.ElementType
  label: string
}) {
  return (
    <button type="button" onClick={onClick}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border-b-2 transition-colors',
              active ? 'border-cyan text-text-primary' : 'border-transparent text-text-muted hover:text-text-secondary',
            )}>
      <Icon size={12} /> {label}
    </button>
  )
}


function DiffCell({ before, after }: { before: any, after: any }) {
  const fmt = (v: any) => v == null ? '—' : (typeof v === 'object' ? JSON.stringify(v) : String(v))
  const beforeS = fmt(before)
  const afterS = fmt(after)
  return (
    <div className="flex items-center gap-1 text-[10px]">
      <span className="text-text-muted line-through truncate max-w-[200px]">{beforeS.slice(0, 60)}</span>
      <span className="text-text-muted">→</span>
      <span className="text-emerald truncate max-w-[200px]">{afterS.slice(0, 60)}</span>
    </div>
  )
}


function DT({ label, children }: { label: string, children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-[9px] uppercase tracking-widest text-text-muted">{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}


function daysSince(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const days = Math.floor(ms / 86400000)
  if (days <= 0) {
    const hours = Math.floor(ms / 3600000)
    if (hours <= 0) return 'just now'
    return `${hours}h`
  }
  return `${days}d`
}
